import React, { useRef, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { t } from '../../lib/i18n';
import {
  Star, Copy, Trash2, User
} from 'lucide-react';

// ─── DATA ────────────────────────────────────────────────────────────────────

const RARITIES = {
  common:    { color: '#8a8a8a', glow: 'rgba(138,138,138,0.4)' },
  uncommon:  { color: '#1eff00', glow: 'rgba(30,255,0,0.4)' },
  rare:      { color: '#0070dd', glow: 'rgba(0,112,221,0.5)' },
  very_rare: { color: '#a335ee', glow: 'rgba(163,53,238,0.5)' },
  legendary: { color: '#ff8000', glow: 'rgba(255,128,0,0.5)', pulse: true },
  artifact:  { color: '#e6001a', glow: 'rgba(230,0,26,0.5)', pulse: true },
};

const TYPE_EMOJIS = {
  weapon: '\u2694\uFE0F', armor: '\uD83D\uDEE1\uFE0F', potion: '\uD83E\uDDEA',
  scroll: '\uD83D\uDCDC', wondrous: '\u2728', ring: '\uD83D\uDC8D',
  wand: '\uD83E\uDE84', staff: '\uD83E\uDE84', ammunition: '\uD83C\uDFF9',
  tool: '\uD83D\uDD27', material: '\uD83D\uDCE6', quest_item: '\uD83D\uDC51'
};

const SLOT_LABELS = {
  fr: { none: '', head: 'T\u00eate', chest: 'Torse', hands: 'Mains', feet: 'Pieds', neck: 'Cou', ring: 'Doigt', waist: 'Ceinture', back: 'Dos', main_hand: 'Main princ.', off_hand: 'Main second.', two_hands: 'Deux mains' },
  en: { none: '', head: 'Head', chest: 'Chest', hands: 'Hands', feet: 'Feet', neck: 'Neck', ring: 'Finger', waist: 'Waist', back: 'Back', main_hand: 'Main hand', off_hand: 'Off-hand', two_hands: 'Two hands' }
};

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export default function Equipment3DCard({
  item,
  language = 'fr',
  onEdit,
  onToggleFavorite,
  onDuplicate,
  onDelete,
  readOnly = false
}) {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const tiltX = ((y - 50) / 50) * 12;
    const tiltY = ((x - 50) / 50) * -12;
    setMousePos({ x, y });
    setTilt({ x: tiltX, y: tiltY });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setTilt({ x: 0, y: 0 });
    setMousePos({ x: 50, y: 50 });
  }, []);

  const rarity = RARITIES[item.rarity] || RARITIES.common;
  const typeEmoji = TYPE_EMOJIS[item.type] || '\u2728';
  const slot = (SLOT_LABELS[language] || SLOT_LABELS.fr)[item.slot] || '';
  const assigned = item.assigned_to || [];
  const isHolo = !!item.is_holographic;
  const hasPulse = rarity.pulse;

  return (
    <div
      ref={cardRef}
      className={cn(
        'ttrpg-card',
        hasPulse && 'pulse-border',
        isHovered && 'hovered'
      )}
      style={{
        '--rarity-color': rarity.color,
        '--rarity-glow': rarity.glow,
        '--tilt-x': `${tilt.x}deg`,
        '--tilt-y': `${tilt.y}deg`,
        '--mouse-x': `${mousePos.x}%`,
        '--mouse-y': `${mousePos.y}%`,
        '--hue-angle': `${(mousePos.x + mousePos.y) * 1.8}deg`,
      }}
      onMouseMove={(e) => { setIsHovered(true); handleMouseMove(e); }}
      onMouseLeave={handleMouseLeave}
      onClick={() => onEdit?.(item)}
    >
      {/* Holographic overlay */}
      {isHolo && <div className="holo-overlay" />}
      {isHolo && isHovered && <div className="sparkles-overlay" />}

      {/* Card inner */}
      <div className="card-inner">
        {/* Art zone */}
        <div className="card-art-zone">
          <span className="card-type-icon">{typeEmoji}</span>
          {item.charges != null && item.charges > 0 && (
            <span className="card-charges">{item.charges}/{item.max_charges || '?'}</span>
          )}
          {item.image_url ? (
            <img src={item.image_url} alt={item.name} className="card-art-img" />
          ) : (
            <div className="card-art-placeholder">
              <span style={{ fontSize: '3rem' }}>{typeEmoji}</span>
            </div>
          )}
          {/* Favorite star */}
          {item.is_favorite && (
            <span className="card-fav-star">\u2B50</span>
          )}
        </div>

        <div className="card-sep" />

        {/* Name */}
        <div className="card-name-zone">
          <span className="card-name">{item.name}</span>
          {item.bonus && (
            <span className="card-bonus">{item.bonus}</span>
          )}
        </div>

        <div className="card-sep" />

        {/* Type line */}
        <div className="card-type-line">
          <span>{t(`rarity_${item.rarity}`, language)} — {t(`equipment_type_${item.type}`, language)}</span>
          {slot && <span className="card-emplacement">{slot}</span>}
        </div>

        <div className="card-sep" />

        {/* Text box */}
        <div className="card-text-zone">
          {item.description && (
            <p className="card-desc-text">{item.description}</p>
          )}
          {item.effect && (
            <p className="card-effect-text">{item.effect}</p>
          )}
          {!item.description && !item.effect && (
            <p className="card-desc-text" style={{ opacity: 0.3, textAlign: 'center' }}>
              {language === 'fr' ? 'Aucune description' : 'No description'}
            </p>
          )}
        </div>

        <div className="card-sep" />

        {/* Footer */}
        <div className="card-footer">
          {item.value_gp > 0 && (
            <span className="card-footer-stat">{item.value_gp} PO</span>
          )}
          {item.weight > 0 && (
            <span className="card-footer-stat">{item.weight} lb</span>
          )}
          {assigned.length > 0 && (
            <span className="card-footer-owner">
              <User style={{ width: 11, height: 11, display: 'inline', verticalAlign: '-2px' }} />{' '}
              {assigned[0].character_name || assigned[0].username}
              {assigned.length > 1 && ` +${assigned.length - 1}`}
            </span>
          )}
        </div>
      </div>

      {/* GM hover actions */}
      {!readOnly && (
        <div className="card-hover-actions">
          <Button variant="ghost" size="icon" className="h-7 w-7 bg-black/60 backdrop-blur-sm border border-white/10 hover:bg-black/80"
            onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(item.id); }}>
            <Star className={cn("h-3.5 w-3.5 text-white", item.is_favorite && "fill-amber-400 text-amber-400")} />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 bg-black/60 backdrop-blur-sm border border-white/10 hover:bg-black/80"
            onClick={(e) => { e.stopPropagation(); onDuplicate?.(item.id); }}>
            <Copy className="h-3.5 w-3.5 text-white" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 bg-black/60 backdrop-blur-sm border border-white/10 hover:bg-red-900/80"
            onClick={(e) => { e.stopPropagation(); onDelete?.(item); }}>
            <Trash2 className="h-3.5 w-3.5 text-red-400" />
          </Button>
        </div>
      )}
    </div>
  );
}
